import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { rpc } from "../../rpc";
import type { CommentTargetType, ReactionSummary } from "../../types";

// ============================================================================
// Quick-react emojis (shown as one-click shortcuts at the top of the picker)
// ============================================================================

const QUICK_REACT_EMOJIS = ["👍", "❤️", "🎉", "🔥", "👀", "🚀"];

// ============================================================================
// Per-user emoji usage tracking (localStorage)
// ============================================================================

const EMOJI_USAGE_KEY = "pulse_emoji_usage";
const MAX_RECENT = 10;

type EmojiUsage = { emoji: string; count: number; lastUsed: number };

function getEmojiUsage(): EmojiUsage[] {
  try {
    const raw = localStorage.getItem(EMOJI_USAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function recordEmojiUsage(emoji: string) {
  const usage = getEmojiUsage();
  const existing = usage.find((u) => u.emoji === emoji);
  if (existing) {
    existing.count++;
    existing.lastUsed = Date.now();
  } else {
    usage.push({ emoji, count: 1, lastUsed: Date.now() });
  }
  try {
    localStorage.setItem(EMOJI_USAGE_KEY, JSON.stringify(usage));
  } catch {
    // localStorage full or unavailable
  }
}

/** Returns user's top N emojis by frequency+recency, falling back to QUICK_REACT_EMOJIS */
function getUserQuickEmojis(): string[] {
  const usage = getEmojiUsage();
  if (usage.length === 0) return QUICK_REACT_EMOJIS.slice(0, MAX_RECENT);
  // Sort by count desc, then lastUsed desc
  const sorted = [...usage].sort(
    (a, b) => b.count - a.count || b.lastUsed - a.lastUsed,
  );
  const top = sorted.slice(0, MAX_RECENT).map((u) => u.emoji);
  // Pad with defaults if user hasn't used enough emojis yet
  for (const e of QUICK_REACT_EMOJIS) {
    if (top.length >= MAX_RECENT) break;
    if (!top.includes(e)) top.push(e);
  }
  return top;
}

// ============================================================================
// Emoji catalog with search keywords
// ============================================================================

const EMOJI_CATALOG: { emoji: string; keywords: string[] }[] = [
  // Gestures
  {
    emoji: "👍",
    keywords: ["thumbs up", "like", "yes", "good", "ok", "approve"],
  },
  { emoji: "👏", keywords: ["clap", "applause", "bravo", "congrats"] },
  { emoji: "🙌", keywords: ["hands", "raised", "celebrate", "hooray", "yay"] },
  {
    emoji: "🙏",
    keywords: ["pray", "thanks", "thank you", "please", "grateful", "hope"],
  },
  { emoji: "💪", keywords: ["strong", "muscle", "power", "flex", "strength"] },
  {
    emoji: "🤝",
    keywords: ["handshake", "deal", "agree", "partner", "collaborate"],
  },
  { emoji: "👋", keywords: ["wave", "hello", "hi", "bye", "greet"] },
  // Smileys
  { emoji: "😊", keywords: ["smile", "happy", "blush", "pleased", "glad"] },
  { emoji: "😄", keywords: ["grin", "happy", "laugh", "joy", "smile"] },
  { emoji: "😂", keywords: ["laugh", "cry", "funny", "lol", "tears"] },
  { emoji: "🥰", keywords: ["love", "hearts", "adore", "sweet", "affection"] },
  {
    emoji: "🤩",
    keywords: ["star", "eyes", "wow", "amazing", "excited", "starstruck"],
  },
  { emoji: "😎", keywords: ["cool", "sunglasses", "confident", "chill"] },
  { emoji: "🤔", keywords: ["think", "hmm", "wonder", "consider", "curious"] },
  { emoji: "😮", keywords: ["wow", "surprised", "shocked", "oh", "whoa"] },
  // Hearts & symbols
  { emoji: "❤️", keywords: ["heart", "love", "red"] },
  { emoji: "💯", keywords: ["hundred", "100", "perfect", "score", "full"] },
  { emoji: "🔥", keywords: ["fire", "hot", "lit", "awesome", "flame"] },
  { emoji: "⭐", keywords: ["star", "favorite", "gold", "excellent"] },
  { emoji: "✨", keywords: ["sparkle", "shine", "magic", "new", "clean"] },
  {
    emoji: "💡",
    keywords: ["idea", "lightbulb", "insight", "think", "bright"],
  },
  { emoji: "🏆", keywords: ["trophy", "winner", "champion", "award", "first"] },
  {
    emoji: "🎯",
    keywords: ["target", "bullseye", "goal", "hit", "focus", "direct"],
  },
  { emoji: "💎", keywords: ["gem", "diamond", "precious", "value", "quality"] },
  // Celebrations
  {
    emoji: "🎉",
    keywords: ["party", "celebrate", "tada", "congrats", "hooray"],
  },
  { emoji: "🥳", keywords: ["party", "celebrate", "birthday", "woohoo"] },
  { emoji: "🍾", keywords: ["champagne", "celebrate", "pop", "cheers"] },
  // Energy
  {
    emoji: "🚀",
    keywords: ["rocket", "launch", "fast", "go", "ship", "deploy"],
  },
  {
    emoji: "⚡",
    keywords: ["lightning", "fast", "energy", "power", "quick", "zap"],
  },
  { emoji: "🌟", keywords: ["star", "glow", "shine", "bright", "standout"] },
  // Extra (people, nature)
  { emoji: "👀", keywords: ["eyes", "look", "see", "watch", "interesting"] },
  { emoji: "🎶", keywords: ["music", "notes", "song", "vibe"] },
  { emoji: "🌈", keywords: ["rainbow", "color", "pride", "diverse"] },
  {
    emoji: "🤞",
    keywords: ["fingers crossed", "hope", "luck", "wish", "pray"],
  },
];

// ============================================================================
// Types
// ============================================================================

type ReactionBarProps = {
  workspaceId: string;
  targetType: CommentTargetType;
  targetId: string;
  /** Initial reactions (optional, will fetch if not provided) */
  initialReactions?: ReactionSummary[];
  /** Whether reactions are read-only (e.g., in mimic mode) */
  readOnly?: boolean;
  /** Compact mode - hide picker button when no reactions */
  compact?: boolean;
};

type ReactionChipProps = {
  emoji: string;
  count: number;
  userReacted: boolean;
  memberNames: string[];
  onClick: () => void;
  disabled?: boolean;
  animating?: boolean;
};

type ReactionPickerProps = {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  existingEmojis: Set<string>;
};

// ============================================================================
// ReactionChip - Single reaction display
// ============================================================================

function ReactionChip({
  emoji,
  count,
  userReacted,
  memberNames,
  onClick,
  disabled,
  animating,
}: ReactionChipProps) {
  const tooltipText =
    memberNames.length > 4
      ? `${memberNames.slice(0, 3).join(", ")}, and ${memberNames.length - 3} more`
      : memberNames.join(", ");

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltipText}
      class={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all duration-150 ${
        userReacted
          ? "bg-[var(--brand-100)] dark:bg-[var(--brand-900)]/40 text-[var(--brand-700)] dark:text-[var(--brand-300)] border border-[var(--brand-300)] dark:border-[var(--brand-700)]"
          : "bg-surface-muted/70 text-text-muted hover:bg-surface-muted border border-transparent"
      } ${disabled ? "cursor-default" : "cursor-pointer hover:scale-105 active:scale-95"} ${animating ? "animate-[reaction-pop_300ms_ease-out]" : ""}`}
    >
      <span class="text-sm leading-none">{emoji}</span>
      <span class="text-[11px] font-medium tabular-nums leading-none">
        {count}
      </span>
    </button>
  );
}

// ============================================================================
// ReactionPicker - Emoji selection popover with quick-react row
// ============================================================================

function ReactionPicker({
  onSelect,
  onClose,
  existingEmojis,
}: ReactionPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const search = useSignal("");
  const showFullGrid = useSignal(false);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close on escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Focus search when full grid is shown
  useEffect(() => {
    if (showFullGrid.value) {
      searchRef.current?.focus();
    }
  }, [showFullGrid.value]);

  const query = search.value.toLowerCase().trim();
  const filtered = query
    ? EMOJI_CATALOG.filter((e) => e.keywords.some((k) => k.includes(query)))
    : EMOJI_CATALOG;

  // When searching, always show the full grid
  const isExpanded = showFullGrid.value || query.length > 0;

  return (
    <div
      ref={pickerRef}
      class="absolute left-0 bottom-full z-50 mb-2 bg-surface border border-border rounded-xl shadow-lg w-72 animate-[fade-in_100ms_ease-out]"
    >
      {/* Quick-react row — always visible */}
      <div class="p-2 flex items-center gap-1">
        {QUICK_REACT_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => {
              onSelect(emoji);
              onClose();
            }}
            class={`flex-1 flex items-center justify-center p-1.5 rounded-lg text-lg transition-all hover:scale-110 active:scale-95 ${
              existingEmojis.has(emoji)
                ? "bg-[var(--brand-100)] dark:bg-[var(--brand-900)]/40"
                : "hover:bg-surface-muted"
            }`}
            title={
              EMOJI_CATALOG.find((e) => e.emoji === emoji)?.keywords[0] ?? emoji
            }
          >
            {emoji}
          </button>
        ))}
        {/* Expand/more button */}
        <button
          type="button"
          onClick={() => {
            showFullGrid.value = !showFullGrid.value;
          }}
          class="flex-1 flex items-center justify-center p-1.5 rounded-lg text-sm text-text-muted hover:bg-surface-muted hover:text-text transition-colors"
          title="More emojis"
        >
          <svg
            class={`size-5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </button>
      </div>

      {/* Full grid (expandable) */}
      {isExpanded && (
        <>
          <div class="px-2 pb-2">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search emoji..."
              value={search.value}
              onInput={(e) => {
                search.value = (e.target as HTMLInputElement).value;
              }}
              class="w-full px-2.5 py-1.5 text-sm bg-surface-muted/50 border border-border-muted rounded-lg outline-none focus:border-[var(--brand-400)] placeholder:text-text-subtle"
            />
          </div>
          <div class="px-1.5 pb-1.5 grid grid-cols-8 gap-0.5 max-h-48 overflow-y-auto border-t border-border-muted pt-1.5">
            {filtered.map((entry) => (
              <button
                key={entry.emoji}
                type="button"
                onClick={() => {
                  onSelect(entry.emoji);
                  onClose();
                }}
                title={entry.keywords[0]}
                class={`p-1.5 rounded-lg hover:bg-surface-muted transition-colors text-base text-center hover:scale-110 active:scale-95 ${
                  existingEmojis.has(entry.emoji)
                    ? "bg-[var(--brand-100)] dark:bg-[var(--brand-900)]/40"
                    : ""
                }`}
              >
                {entry.emoji}
              </button>
            ))}
            {filtered.length === 0 && (
              <div class="col-span-8 py-3 text-center text-xs text-text-subtle">
                No emoji found
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// ReactionBar - Shows reactions and allows adding reactions
// ============================================================================

export function ReactionBar({
  workspaceId,
  targetType,
  targetId,
  initialReactions,
  readOnly = false,
  compact = false,
}: ReactionBarProps) {
  const reactions = useSignal<ReactionSummary[]>(initialReactions ?? []);
  const showPicker = useSignal(false);
  const isLoading = useSignal(!initialReactions);
  const lastAdded = useSignal<string | null>(null);

  // Load reactions if not provided
  useEffect(() => {
    if (initialReactions) return;

    async function load() {
      try {
        const result = await rpc.reactions.getSummary({
          workspaceId,
          targetType,
          targetId,
        });
        reactions.value = result.summary;
      } catch (err) {
        console.error("Failed to load reactions:", err);
      } finally {
        isLoading.value = false;
      }
    }
    load();
  }, [workspaceId, targetType, targetId, initialReactions]);

  // Clear animation flag after duration
  useEffect(() => {
    if (!lastAdded.value) return;
    const timer = setTimeout(() => {
      lastAdded.value = null;
    }, 300);
    return () => clearTimeout(timer);
  }, [lastAdded.value]);

  const handleToggleReaction = async (emoji: string) => {
    if (readOnly) return;

    // Track emoji usage for personalized quick-react
    recordEmojiUsage(emoji);

    // Optimistic update
    const wasReacted = reactions.value.find(
      (r) => r.emoji === emoji,
    )?.userReacted;

    if (!wasReacted) {
      lastAdded.value = emoji;
      // Optimistic add
      const existing = reactions.value.find((r) => r.emoji === emoji);
      if (existing) {
        reactions.value = reactions.value.map((r) =>
          r.emoji === emoji
            ? { ...r, count: r.count + 1, userReacted: true }
            : r,
        );
      } else {
        reactions.value = [
          ...reactions.value,
          { emoji, count: 1, memberNames: ["You"], userReacted: true },
        ];
      }
    } else {
      // Optimistic remove
      const existing = reactions.value.find((r) => r.emoji === emoji);
      if (existing && existing.count <= 1) {
        reactions.value = reactions.value.filter((r) => r.emoji !== emoji);
      } else {
        reactions.value = reactions.value.map((r) =>
          r.emoji === emoji
            ? { ...r, count: r.count - 1, userReacted: false }
            : r,
        );
      }
    }

    try {
      const result = await rpc.reactions.toggle({
        workspaceId,
        targetType,
        targetId,
        // Safe: emoji is always from EMOJI_CATALOG, matching server ALLOWED_EMOJIS
        emoji: emoji as never,
      });

      // Reconcile if server state differs from optimistic
      if (result.added && wasReacted) {
        // We thought it was a remove but server added — fix
        const existing = reactions.value.find((r) => r.emoji === emoji);
        if (existing) {
          reactions.value = reactions.value.map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.count + 1, userReacted: true }
              : r,
          );
        }
      } else if (!result.added && !wasReacted) {
        // We thought it was an add but server removed — fix
        const existing = reactions.value.find((r) => r.emoji === emoji);
        if (existing && existing.count <= 1) {
          reactions.value = reactions.value.filter((r) => r.emoji !== emoji);
        } else if (existing) {
          reactions.value = reactions.value.map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.count - 1, userReacted: false }
              : r,
          );
        }
      }
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
      // Reload to get correct state
      try {
        const result = await rpc.reactions.getSummary({
          workspaceId,
          targetType,
          targetId,
        });
        reactions.value = result.summary;
      } catch {
        // Give up on reconciliation
      }
    }
  };

  // Don't show anything if compact mode, no reactions, and read-only
  if (compact && reactions.value.length === 0 && readOnly) {
    return null;
  }

  const existingEmojis = new Set(reactions.value.map((r) => r.emoji));
  const hasReactions = reactions.value.length > 0;

  return (
    <div class="flex items-center gap-1.5 flex-wrap relative">
      {/* Existing reactions */}
      {reactions.value.map((reaction) => (
        <ReactionChip
          key={reaction.emoji}
          emoji={reaction.emoji}
          count={reaction.count}
          userReacted={reaction.userReacted}
          memberNames={reaction.memberNames}
          onClick={() => handleToggleReaction(reaction.emoji)}
          disabled={readOnly}
          animating={lastAdded.value === reaction.emoji}
        />
      ))}

      {/* Add reaction button / inline quick-react */}
      {!readOnly && !isLoading.value && (
        <div class="relative flex items-center gap-0.5">
          {!hasReactions && !compact && (
            <>
              {/* Show user's top emojis as one-click inline pills */}
              {getUserQuickEmojis().map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleToggleReaction(emoji)}
                  class="inline-flex items-center justify-center size-6 rounded-full text-sm opacity-30 hover:opacity-100 hover:bg-surface-muted hover:scale-110 active:scale-95 transition-all"
                  title={
                    EMOJI_CATALOG.find((e) => e.emoji === emoji)?.keywords[0] ??
                    emoji
                  }
                >
                  {emoji}
                </button>
              ))}
            </>
          )}
          {/* "+" button to open full picker */}
          <button
            type="button"
            onClick={() => {
              showPicker.value = !showPicker.value;
            }}
            class="inline-flex items-center justify-center size-5 rounded-full border border-dashed border-text-subtle/30 text-text-subtle hover:border-text-muted hover:text-text-muted hover:bg-surface-muted/50 transition-all duration-150"
            title="More reactions"
          >
            <svg
              class="size-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
          </button>

          {showPicker.value && (
            <ReactionPicker
              onSelect={handleToggleReaction}
              onClose={() => {
                showPicker.value = false;
              }}
              existingEmojis={existingEmojis}
            />
          )}
        </div>
      )}
    </div>
  );
}
